import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useProject } from './ProjectContext';
import { useEnvironment } from './EnvironmentContext';
import { projectEnvironmentService } from '@/services/projectEnvironmentService';
import type {
  ProjectEnvironment,
  ProjectEnvironmentCreate,
  ProjectEnvironmentUpdate,
} from '@/types/projectEnvironment';

/**
 * ProjectEnvironmentContext — tracks the currently-selected deployment target.
 *
 * A ProjectEnvironment joins one Project × one Environment (both org-scoped).
 * DatabaseClusters, Databases, and Catalogs all scope to a PE, so this
 * context is the primary selector for the compute / data / catalog pages.
 *
 * Filtering rule:
 *   - If currentProject is set, we list PEs for that project.
 *   - If currentEnvironment is also set, we narrow to PEs that match both.
 *   - Auto-select the first matching PE if none is saved in localStorage.
 */

interface ProjectEnvironmentContextType {
  currentProjectEnvironment: ProjectEnvironment | null;
  projectEnvironments: ProjectEnvironment[];
  isLoading: boolean;
  error: string | null;
  setCurrentProjectEnvironment: (pe: ProjectEnvironment | null) => void;
  createProjectEnvironment: (
    data: Omit<ProjectEnvironmentCreate, 'projectId' | 'environmentId'> & {
      projectId?: string;
      environmentId?: string;
    },
  ) => Promise<ProjectEnvironment>;
  updateProjectEnvironment: (id: string, data: ProjectEnvironmentUpdate) => Promise<ProjectEnvironment>;
  deleteProjectEnvironment: (id: string) => Promise<void>;
  refreshProjectEnvironments: () => Promise<void>;
}

const ProjectEnvironmentContext = createContext<ProjectEnvironmentContextType | null>(null);

export const useProjectEnvironment = () => {
  const ctx = useContext(ProjectEnvironmentContext);
  if (!ctx) {
    throw new Error('useProjectEnvironment must be used within a ProjectEnvironmentProvider');
  }
  return ctx;
};

interface ProjectEnvironmentProviderProps {
  children: React.ReactNode;
}

export const ProjectEnvironmentProvider = ({ children }: ProjectEnvironmentProviderProps) => {
  const { currentProject } = useProject();
  const { currentEnvironment } = useEnvironment();
  const [projectEnvironments, setProjectEnvironments] = useState<ProjectEnvironment[]>([]);
  const [currentProjectEnvironment, setCurrentProjectEnvironment] = useState<ProjectEnvironment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const storageKey = currentProject
    ? `gizmodata-current-pe-${currentProject.id}-${currentEnvironment?.id ?? 'any'}`
    : null;

  const load = useCallback(async () => {
    if (!currentProject) {
      setProjectEnvironments([]);
      setCurrentProjectEnvironment(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const list = await projectEnvironmentService.list({
        projectId: currentProject.id,
        environmentId: currentEnvironment?.id,
      });
      setProjectEnvironments(list);

      const savedId = storageKey ? localStorage.getItem(storageKey) : null;
      if (savedId) {
        const saved = list.find(pe => pe.id === savedId);
        if (saved) {
          setCurrentProjectEnvironment(saved);
          return;
        }
      }
      setCurrentProjectEnvironment(list.length > 0 ? list[0] : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project environments');
      setProjectEnvironments([]);
      setCurrentProjectEnvironment(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentProject, currentEnvironment, storageKey]);

  useEffect(() => {
    load();
  }, [load]);

  // Only save a target that matches the key's project (and environment): right after a project or
  // environment switch the previous target is still selected, and saving it would overwrite the new
  // key's remembered target.
  useEffect(() => {
    if (
      storageKey &&
      currentProjectEnvironment &&
      currentProjectEnvironment.projectId === currentProject?.id &&
      (!currentEnvironment || currentProjectEnvironment.environmentId === currentEnvironment.id)
    ) {
      localStorage.setItem(storageKey, currentProjectEnvironment.id);
    }
  }, [storageKey, currentProjectEnvironment, currentProject, currentEnvironment]);

  const createProjectEnvironment: ProjectEnvironmentContextType['createProjectEnvironment'] = async data => {
    const projectId = data.projectId ?? currentProject?.id;
    const environmentId = data.environmentId ?? currentEnvironment?.id;
    if (!projectId || !environmentId) {
      throw new Error('createProjectEnvironment requires both a project and an environment to be selected.');
    }
    const created = await projectEnvironmentService.create({
      name: data.name,
      label: data.label,
      projectId,
      environmentId,
      defaultCloudRegionId: data.defaultCloudRegionId,
    });
    setProjectEnvironments(prev => [...prev, created]);
    if (projectEnvironments.length === 0) {
      setCurrentProjectEnvironment(created);
    }
    return created;
  };

  const updateProjectEnvironment = async (id: string, data: ProjectEnvironmentUpdate) => {
    const updated = await projectEnvironmentService.update(id, data);
    setProjectEnvironments(prev => prev.map(pe => (pe.id === id ? updated : pe)));
    if (currentProjectEnvironment?.id === id) setCurrentProjectEnvironment(updated);
    return updated;
  };

  const deleteProjectEnvironment = async (id: string) => {
    await projectEnvironmentService.delete(id);
    setProjectEnvironments(prev => prev.filter(pe => pe.id !== id));
    if (currentProjectEnvironment?.id === id) {
      const remaining = projectEnvironments.filter(pe => pe.id !== id);
      setCurrentProjectEnvironment(remaining[0] ?? null);
    }
  };

  const refreshProjectEnvironments = load;

  return (
    <ProjectEnvironmentContext.Provider
      value={{
        currentProjectEnvironment,
        projectEnvironments,
        isLoading,
        error,
        setCurrentProjectEnvironment,
        createProjectEnvironment,
        updateProjectEnvironment,
        deleteProjectEnvironment,
        refreshProjectEnvironments,
      }}
    >
      {children}
    </ProjectEnvironmentContext.Provider>
  );
};
